import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Players from './pages/Players';
import PlayerDetail from './pages/PlayerDetail';
import PlayerForm from './pages/PlayerForm';
import BulkImport from './pages/BulkImport';
import Clubs from './pages/Clubs';
import ClubDetail from './pages/ClubDetail';
import ClubForm from './pages/ClubForm';
import Transfers from './pages/Transfers';
import TransferDetail from './pages/TransferDetail';
import TransferForm from './pages/TransferForm';
import Tasks from './pages/Tasks';
import Calendar from './pages/Calendar';
import AgencyFees from './pages/AgencyFees';
import Messages from './pages/Messages';
import ClientTrackers from './pages/ClientTrackers';
import ClubRequests from './pages/ClubRequests';
import ClubMeetings from './pages/ClubMeetings';
import Fixtures from './pages/Fixtures';
import FixturesDiary from './pages/FixturesDiary';
import UserManagement from './pages/UserManagement';
import EmailSettings from './pages/EmailSettings';
import Staff from './pages/Staff';
import StaffDetail from './pages/StaffDetail';
import StaffForm from './pages/StaffForm';
import PlayerPortal from './pages/PlayerPortal';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);

    // Listen for PWA install prompt
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!localStorage.getItem('pwa-dismissed')) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setInstallPrompt(null);
  };

  const dismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-dismissed', 'true');
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
      <div className="text-white">Loading...</div>
    </div>;
  }

  return (
    <div className="App">
      {showInstallBanner && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-white text-black px-4 py-3 flex items-center justify-center gap-4 shadow-lg" data-testid="pwa-install-banner">
          <span className="text-sm font-bold">Install DC Sports & Talent on your device for quick access</span>
          <button onClick={handleInstall} className="bg-black text-white px-4 py-1.5 rounded-sm text-xs font-bold uppercase hover:bg-zinc-800 transition-colors" data-testid="pwa-install-button">
            Install App
          </button>
          <button onClick={dismissInstall} className="text-black/60 hover:text-black text-lg leading-none" data-testid="pwa-dismiss-button">&times;</button>
        </div>
      )}
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
          {user?.role === 'player' ? (
            <>
              <Route path="/" element={<PlayerPortal />} />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          ) : (
            <Route element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
              <Route path="/" element={<Dashboard />} />
            <Route path="/players" element={<Players />} />
            <Route path="/players/new" element={<PlayerForm />} />
            <Route path="/players/bulk-import" element={<BulkImport />} />
            <Route path="/players/:id" element={<PlayerDetail />} />
            <Route path="/players/:id/edit" element={<PlayerForm />} />
            <Route path="/clubs" element={<Clubs />} />
            <Route path="/clubs/new" element={<ClubForm />} />
            <Route path="/clubs/:id" element={<ClubDetail />} />
            <Route path="/clubs/:id/edit" element={<ClubForm />} />
            <Route path="/transfers" element={<Transfers />} />
            <Route path="/transfers/new" element={<TransferForm />} />
            <Route path="/transfers/:id" element={<TransferDetail />} />
            <Route path="/transfers/:id/edit" element={<TransferForm />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/agency-fees" element={<AgencyFees />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/client-trackers" element={<ClientTrackers />} />
            <Route path="/club-requests" element={<ClubRequests />} />
            <Route path="/club-meetings" element={<ClubMeetings />} />
            <Route path="/fixtures" element={<Fixtures />} />
            <Route path="/fixtures-diary" element={<FixturesDiary />} />
            <Route path="/staff" element={<Staff />} />
            <Route path="/staff/new" element={<StaffForm />} />
            <Route path="/staff/:id" element={<StaffDetail />} />
            <Route path="/staff/:id/edit" element={<StaffForm />} />
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/email-settings" element={<EmailSettings />} />
            </Route>
          )}
          {!user && <Route path="*" element={<Navigate to="/login" />} />}
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;